import React from "react";
import PropTypes from "prop-types";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import TimePicker from "components/common/TimePicker";
import Text from "components/common/Text";

import "./TimeRangeSelector.scss";

function TimeRangeSelector({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  showNextDayLabel,
  nextDayLabel,
  nextDayLabelClass,
  helperText,
  helperTextClassName,
  startLabel,
  endLabel,
  nextDayText,
  startPlaceholder,
  endPlaceholder,
  startTimeError,
  endTimeError,
  breakSlotsError,
  size,
  showArrow,
  disabled,
  startTimeDisabled,
  endTimeDisabled,
  heading,
  totalDuration,
  showHeading,
  multipleSlots,
  slots,
  onAddSlot,
  onRemoveSlot,
  onSlotChange,
  addSlotButtonText,
  hideAddButton,
  arrowClassName = "time-range-selector__arrow",
  timeRangeSelectorContainerClassName = "time-range-selector__container",
}) {
  return (
    <div className="time-range-selector">
      <div className={timeRangeSelectorContainerClassName}>
        {showHeading && heading && (
          <div className="time-range-selector__header">
            <h3 className="time-range-selector__heading">{heading}</h3>
            {totalDuration && (
              <p className="time-range-selector__total para-body-m-regular">
                Total duration <span className="heading-h3-semibold">{totalDuration}</span>
              </p>
            )}
          </div>
        )}
        {helperText && (
          <p className={`time-range-selector__helper-text ${helperTextClassName || "para-body-m-medium"}`}>
            {helperText}
          </p>
        )}

        {multipleSlots ? (
          <>
            {slots.map((slot) => (
              <div key={slot.id} className="time-range-selector__slot">
                <div
                  className={`time-range-selector__field-group ${!disabled && slots.length > 1 ? "with-remove-button" : ""}`}
                >
                  <TimePicker
                    name="startTime"
                    label={startLabel}
                    value={slot.startTime}
                    onChange={(value) => onSlotChange(slot.id, "startTime", value)}
                    placeholder={startPlaceholder}
                    size={size}
                    disabled={disabled}
                    error={!!startTimeError}
                  />

                  {showArrow && (
                    <div className={arrowClassName}>
                      <ArrowForwardIcon />
                    </div>
                  )}

                  <TimePicker
                    name="endTime"
                    label={endLabel}
                    value={slot.endTime}
                    onChange={(value) => onSlotChange(slot.id, "endTime", value)}
                    placeholder={endPlaceholder}
                    size={size}
                    disabled={disabled}
                  />

                  {showNextDayLabel && (
                    <Text
                      label={nextDayLabel || ""}
                      labelClass={nextDayLabelClass || ""}
                      name="nextDay"
                      value={nextDayText}
                      size="small"
                      disabled
                    />
                  )}

                  {!disabled && slots.length > 1 && (
                    <div className="time-range-selector__remove-button">
                      <IconButton size="small" onClick={() => onRemoveSlot(slot.id)} className="remove-slot-button">
                        <CloseIcon />
                      </IconButton>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {breakSlotsError && <p className="time-range-selector__break-slots-error">{breakSlotsError}</p>}

            {!disabled && !hideAddButton && (
              <button type="button" className="time-range-selector__add-button" onClick={onAddSlot}>
                {addSlotButtonText}
                <AddIcon />
              </button>
            )}
          </>
        ) : (
          <div className="time-range-selector__field-group">
            <TimePicker
              name="startTime"
              label={startLabel}
              value={startTime}
              onChange={onStartTimeChange}
              placeholder={startPlaceholder}
              size={size}
              error={!!startTimeError}
              helperText={startTimeError}
              disabled={startTimeDisabled !== undefined ? startTimeDisabled : disabled}
            />

            {showArrow && (
              <div className={arrowClassName}>
                <ArrowForwardIcon />
              </div>
            )}

            <TimePicker
              name="endTime"
              label={endLabel}
              value={endTime}
              onChange={onEndTimeChange}
              placeholder={endPlaceholder}
              size={size}
              disabled={endTimeDisabled !== undefined ? endTimeDisabled : disabled}
              error={!!endTimeError}
              helperText={endTimeError}
            />

            {showNextDayLabel && (
              <Text
                label=" "
                labelClass={nextDayLabelClass || ""}
                name="nextDay"
                value={nextDayText}
                size="small"
                disabled
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

TimeRangeSelector.propTypes = {
  startTime: PropTypes.instanceOf(Date),
  endTime: PropTypes.instanceOf(Date),
  onStartTimeChange: PropTypes.func,
  onEndTimeChange: PropTypes.func,
  showNextDayLabel: PropTypes.bool,
  nextDayLabel: PropTypes.string,
  nextDayLabelClass: PropTypes.string,
  helperText: PropTypes.string,
  helperTextClassName: PropTypes.string,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  nextDayText: PropTypes.string,
  startPlaceholder: PropTypes.string,
  endPlaceholder: PropTypes.string,
  endTimeError: PropTypes.string,
  startTimeError: PropTypes.string,
  breakSlotsError: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  showArrow: PropTypes.bool,
  disabled: PropTypes.bool,
  startTimeDisabled: PropTypes.bool,
  endTimeDisabled: PropTypes.bool,
  heading: PropTypes.string,
  totalDuration: PropTypes.string,
  showHeading: PropTypes.bool,
  multipleSlots: PropTypes.bool,
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
      date: PropTypes.instanceOf(Date),
    })
  ),
  onAddSlot: PropTypes.func,
  onRemoveSlot: PropTypes.func,
  onSlotChange: PropTypes.func,
  addSlotButtonText: PropTypes.string,
  hideAddButton: PropTypes.bool,
};

TimeRangeSelector.defaultProps = {
  startTime: null,
  endTime: null,
  onStartTimeChange: null,
  onEndTimeChange: null,
  showNextDayLabel: false,
  helperText: "Please enter all times in your local timezone.",
  helperTextClassName: "",
  startLabel: "Start shift time",
  endLabel: "End shift time",
  nextDayText: "Next day",
  nextDayLabel: "",
  nextDayLabelClass: "",
  startPlaceholder: "hh:mm aa",
  endPlaceholder: "hh:mm aa",
  endTimeError: "",
  startTimeError: "",
  breakSlotsError: "",
  size: "small",
  showArrow: true,
  disabled: false,
  startTimeDisabled: undefined,
  endTimeDisabled: undefined,
  heading: "",
  totalDuration: "",
  showHeading: false,
  multipleSlots: false,
  slots: [],
  onAddSlot: null,
  onRemoveSlot: null,
  onSlotChange: null,
  addSlotButtonText: "Add slot",
  hideAddButton: false,
};

export default TimeRangeSelector;
